import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import type { RootState } from "../../../store/store";

import {
  DpProcessKind,
  DpProcessListItem,
  DpProcessStatus,
  listProcessesApi,
  PROCESS_KIND_LABELS,
  PROCESS_STATUS_BADGE,
  PROCESS_STATUS_LABELS,
} from "../api/processesApi";
import { formatDateOnly } from "../utils/format";
import { DP_EMPLOYEES_PAGE, DP_EXPIRING_DOCUMENTS_PAGE, DP_PAGE, DP_PROCESS_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

const LIMIT = 20;

/** Admissões e desligamentos de todos os colaboradores. Abrir um processo é pela ficha do colaborador. */
export default function ProcessesPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [kind, setKind] = useState<DpProcessKind | "">("");
  const [status, setStatus] = useState<DpProcessStatus | "">("EM_ANDAMENTO");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [page, setPage] = useState(1);

  const [items, setItems] = useState<DpProcessListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await listProcessesApi(token, {
        page,
        limit: LIMIT,
        kind: kind || undefined,
        status: status || undefined,
        overdueOnly,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os processos.");
    } finally {
      setLoading(false);
    }
  }, [token, page, kind, status, overdueOnly]);

  useEffect(() => {
    load();
  }, [load]);

  if (!token) return null;

  const done = (p: DpProcessListItem) => p.totalSteps - p.pendingSteps;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[
              { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
              { label: "Admissão e desligamento" },
            ]}
            title="Admissão e desligamento"
            subtitle="Checklists em andamento e concluídos. Para iniciar um, abra a ficha do colaborador."
            onBack={goBack}
            backLabel="Voltar para o DP"
            actions={
              <>
                <button type="button" className="ecoBtn" onClick={() => navigate(DP_EXPIRING_DOCUMENTS_PAGE())}>
                  <EcoIcon name="file" size={18} />
                  Documentos a vencer
                </button>
                <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => navigate(DP_EMPLOYEES_PAGE())}>
                  <EcoIcon name="users" size={18} />
                  Colaboradores
                </button>
              </>
            }
          />

          <section className="ecoCard dpeList" aria-label="Processos">
            <div className="dpeList__toolbar">
              <div className="dpeList__filters">
                <label htmlFor="proc-kind" className="ecoVisuallyHidden">Tipo</label>
                <select
                  id="proc-kind"
                  className="ecoSelect ecoSelect--sm dpeList__select"
                  value={kind}
                  onChange={(e) => {
                    setPage(1);
                    setKind(e.target.value as DpProcessKind | "");
                  }}
                >
                  <option value="">Admissões e desligamentos</option>
                  {(Object.keys(PROCESS_KIND_LABELS) as DpProcessKind[]).map((k) => (
                    <option key={k} value={k}>
                      {PROCESS_KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
                <label htmlFor="proc-status" className="ecoVisuallyHidden">Situação</label>
                <select
                  id="proc-status"
                  className="ecoSelect ecoSelect--sm dpeList__select"
                  value={status}
                  disabled={overdueOnly}
                  onChange={(e) => {
                    setPage(1);
                    setStatus(e.target.value as DpProcessStatus | "");
                  }}
                >
                  <option value="">Todas as situações</option>
                  {(Object.keys(PROCESS_STATUS_LABELS) as DpProcessStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {PROCESS_STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
                <label className="dpCheckbox">
                  <input
                    type="checkbox"
                    checked={overdueOnly}
                    onChange={(e) => {
                      setPage(1);
                      setOverdueOnly(e.target.checked);
                    }}
                  />
                  Só com etapa atrasada
                </label>
              </div>
              <span className="dpeList__count" aria-live="polite">
                {loading ? "Carregando..." : `${total} ${total === 1 ? "processo" : "processos"}`}
              </span>
            </div>

            {!loading && items.length === 0 ? (
              <div className="dpeList__empty">
                <p className="ecoEmpty">Nenhum processo encontrado.</p>
              </div>
            ) : (
              <>
                <div className="ecoTableWrap dpeList__table">
                  <table className="ecoTable">
                    <thead>
                      <tr>
                        <th scope="col">Colaborador</th>
                        <th scope="col">Processo</th>
                        <th scope="col">Data-base</th>
                        <th scope="col">Etapas</th>
                        <th scope="col">Situação</th>
                        <th scope="col" className="ecoTable__num">Ação</th>
                      </tr>
                    </thead>
                    <tbody aria-busy={loading}>
                      {items.map((p) => (
                        <tr key={p.id}>
                          <td className="dpePerson__name">{p.employee.fullName}</td>
                          <td>
                            <span className="dpePerson__text">
                              <span>{PROCESS_KIND_LABELS[p.kind]}</span>
                              <span className="dpePerson__sub">{p.template?.name ?? "—"}</span>
                            </span>
                          </td>
                          <td className="ecoTable__muted">{formatDateOnly(p.baseDate)}</td>
                          <td>
                            <span className="dpeSteps">
                              <span className="dpeSteps__bar" aria-hidden="true">
                                <span
                                  className="dpeSteps__fill"
                                  style={{ width: `${p.totalSteps ? (done(p) / p.totalSteps) * 100 : 0}%` }}
                                />
                              </span>
                              <span className="dpeSteps__count">
                                {done(p)}/{p.totalSteps}
                              </span>
                              {p.overdueSteps > 0 ? (
                                <span className="dpBadge dpBadge--red">{p.overdueSteps} atrasada(s)</span>
                              ) : null}
                            </span>
                          </td>
                          <td>
                            <span className={`dpBadge ${PROCESS_STATUS_BADGE[p.status]}`}>{PROCESS_STATUS_LABELS[p.status]}</span>
                          </td>
                          <td className="ecoTable__num">
                            <button
                              type="button"
                              className="ecoBtn ecoBtn--sm"
                              onClick={() => navigate(DP_PROCESS_PAGE(p.id))}
                              aria-label={`Abrir ${PROCESS_KIND_LABELS[p.kind].toLowerCase()} de ${p.employee.fullName}`}
                            >
                              Abrir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <ul className="dpeList__cards" aria-busy={loading}>
                  {items.map((p) => (
                    <li key={p.id}>
                      <button type="button" className="dpeList__card" onClick={() => navigate(DP_PROCESS_PAGE(p.id))}>
                        <span className="dpePerson__text" style={{ flex: 1 }}>
                          <span className="dpePerson__name">{p.employee.fullName}</span>
                          <span className="dpePerson__sub">
                            {PROCESS_KIND_LABELS[p.kind]} · {formatDateOnly(p.baseDate)} · {done(p)}/{p.totalSteps} etapas
                          </span>
                        </span>
                        {p.overdueSteps > 0 ? (
                          <span className="dpBadge dpBadge--red">{p.overdueSteps} atrasada(s)</span>
                        ) : (
                          <span className={`dpBadge ${PROCESS_STATUS_BADGE[p.status]}`}>{PROCESS_STATUS_LABELS[p.status]}</span>
                        )}
                        <EcoIcon name="chevronRight" className="dpeList__cardChevron" />
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="dpeList__pager">
              <EcoPagination page={page} pageSize={LIMIT} total={total} onChange={setPage} disabled={loading} />
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
